import { useEffect, useRef, useState, useCallback } from 'react';
import peckSprite from '../Moves/Peck.png';
import './PeckAnimation.css';

/**
 * PECK ANIMATION - Flying-type Move
 * Based on successful Karate Chop and Bone Rush implementations
 * 
 * Animation Sequence:
 * 1. PREPARATION: Player stance preparation
 * 2. ORBIT_START: Peck sprite begins orbiting around target
 * 3. ORBIT_ATTACK: Multiple orbits with increasing speed
 * 4. IMPACT: Final strike on target
 * 5. FADE_OUT: Clean disappearance
 * 
 * Sprite: Peck.png (192x192, single frame)
 * - Single frame: Flying beak/peck attack sprite
 */
export default function PeckAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('idle');

  const transitionTo = useCallback((phase) => {
    setAnimationPhase(phase);
  }, []);

  useEffect(() => {
    if (!playerRef?.current || !opponentRef?.current) return;

    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    const container = playerEl.closest('.battle-container') || document.body;

    // Get positioning data
    const p = playerEl.getBoundingClientRect();
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // SPRITE CONFIGURATION - Peck.png (192x192, single frame)
    const SPRITE_CONFIG = {
      frameWidth: 192,   // Width of sprite (192x192)
      frameHeight: 192,  // Height of sprite
      orbitRadius: 80,   // Radius of orbit around target
    };

    // Calculate perfect center positioning over target
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // TIMING CONFIGURATION - Enhanced for orbiting motion with nudging
    const TIMING = {
      preparation: 400,    // Player nudging and stance preparation (extended)
      orbitStart: 200,     // Initial orbit setup
      orbitAttack: 400,    // Multiple orbits with speed increase
      impact: 250,         // Final impact duration
      fadeOut: 180,        // Clean fade out
    };

    console.log('🪶 Peck Animation - Flying-type Move Starting');
    console.log(`🪶 Target center: (${centerX}, ${centerY})`);

    // PHASE 1: PREPARATION WITH NUDGING (0-400ms)
    setAnimationPhase('preparation');
    
    // Player nudging animation - gives feeling of preparing to peck
    console.log('🪶 Player nudging animation - preparing to peck');
    playerEl.style.transition = 'transform 150ms ease-in-out';
    playerEl.style.transform = 'translateX(8px) scale(1.05) rotate(1deg)'; // Forward nudge
    
    // Second nudge motion
    setTimeout(() => {
      if (playerEl) {
        playerEl.style.transform = 'translateX(-4px) scale(0.98) rotate(-1deg)'; // Pull back
      }
    }, 150);
    
    // Final preparation stance
    setTimeout(() => {
      if (playerEl) {
        playerEl.style.transform = 'scale(1.08) rotate(2deg)'; // Flying-type stance
      }
    }, 300);
    
    // Subtle target highlight preparation
    opponentEl.style.transition = 'filter 150ms ease-in';
    opponentEl.style.filter = 'brightness(1.1)';
    
    // PHASE 2: ORBIT START - Begin orbiting (400-600ms)
    const orbitStartTimer = setTimeout(() => {
      setAnimationPhase('orbit_start');
      console.log('🪶 Phase 2: Orbit Start - Peck begins orbiting');
      
      // Create orbiting peck effect
      const peckEffect = document.createElement('div');
      peckEffect.className = 'peck-orbit-start';
      
      // Start position: top of orbit around target
      const startX = centerX - SPRITE_CONFIG.frameWidth / 2;
      const startY = centerY - SPRITE_CONFIG.orbitRadius - SPRITE_CONFIG.frameHeight / 2;
      
      peckEffect.style.left = `${startX}px`;
      peckEffect.style.top = `${startY}px`;
      peckEffect.style.width = `${SPRITE_CONFIG.frameWidth}px`;
      peckEffect.style.height = `${SPRITE_CONFIG.frameHeight}px`;
      
      // Set Peck sprite
      peckEffect.style.backgroundImage = `url(${peckSprite})`;
      peckEffect.style.backgroundSize = '192px 192px'; // Single frame sprite
      peckEffect.style.backgroundPosition = '0px 0px';
      peckEffect.style.backgroundRepeat = 'no-repeat';
      peckEffect.style.position = 'absolute';
      peckEffect.style.zIndex = '90';
      peckEffect.style.pointerEvents = 'none';
      
      // Set orbit center for CSS animation
      peckEffect.style.setProperty('--orbit-center-x', `${centerX}px`);
      peckEffect.style.setProperty('--orbit-center-y', `${centerY}px`);
      peckEffect.style.setProperty('--orbit-radius', `${SPRITE_CONFIG.orbitRadius}px`);
      
      container.appendChild(peckEffect);
      animRef.current = peckEffect;
      
    }, TIMING.preparation);
    
    // PHASE 3: ORBIT ATTACK - Accelerating orbits (350-750ms)
    const orbitAttackTimer = setTimeout(() => {
      setAnimationPhase('orbit_attack');
      console.log('🪶 Phase 3: Orbit Attack - Accelerating orbits');
      
      if (animRef.current) {
        // Switch to accelerating orbit animation
        animRef.current.className = 'peck-orbit-attack';
        
        console.log('🪶 Peck accelerating orbit around target');
      }
      
    }, TIMING.preparation + TIMING.orbitStart);
    
    // PHASE 4: IMPACT - Final strike (750-1000ms)
    const impactTimer = setTimeout(() => {
      setAnimationPhase('impact');
      console.log('🪶 Phase 4: Impact - Final strike');
      
      if (animRef.current) {
        // Switch to impact animation
        animRef.current.className = 'peck-impact';
        
        // Center on target for impact
        const centerXPos = centerX - SPRITE_CONFIG.frameWidth / 2;
        const centerYPos = centerY - SPRITE_CONFIG.frameHeight / 2;
        animRef.current.style.left = `${centerXPos}px`;
        animRef.current.style.top = `${centerYPos}px`;
        
        console.log('🪶 Final peck strike at target center');
        
        // Impact flash effect
        const flashEffect = document.createElement('div');
        flashEffect.className = 'peck-flash';
        flashEffect.style.left = `${centerX}px`;
        flashEffect.style.top = `${centerY}px`;
        container.appendChild(flashEffect);
        
        // Clean up flash after animation
        setTimeout(() => {
          if (flashEffect.parentNode) {
            flashEffect.parentNode.removeChild(flashEffect);
          }
        }, 250);
        
        // Opponent hit reaction
        opponentEl.classList.add('peck-hit');
      }
      
    }, TIMING.preparation + TIMING.orbitStart + TIMING.orbitAttack);
    
    // PHASE 5: FADE OUT (1000-1180ms)
    const fadeOutTimer = setTimeout(() => {
      setAnimationPhase('fade_out');
      console.log('🪶 Phase 5: Fade Out');
      
      if (animRef.current) {
        animRef.current.className = 'peck-fade-out';
      }
      
    }, TIMING.preparation + TIMING.orbitStart + TIMING.orbitAttack + TIMING.impact);
    
    // PHASE 6: COMPLETE (1180ms)
    const completeTimer = setTimeout(() => {
      setAnimationPhase('complete');
      console.log('🪶 Phase 6: Animation Complete - Flying-type Move');
      
      // Cleanup and reset
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset player and opponent states
      playerEl.style.transition = '';
      playerEl.style.transform = '';
      opponentEl.style.transition = '';
      opponentEl.style.filter = '';
      opponentEl.classList.remove('peck-hit');
      
      onComplete();
      
    }, TIMING.preparation + TIMING.orbitStart + TIMING.orbitAttack + TIMING.impact + TIMING.fadeOut);

    // Enhanced cleanup function
    return () => {
      clearTimeout(orbitStartTimer);
      clearTimeout(orbitAttackTimer);
      clearTimeout(impactTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      if (opponentEl) {
        opponentEl.style.transition = '';
        opponentEl.style.filter = '';
        opponentEl.classList.remove('peck-hit');
      }
      
      console.log('🪶 Peck Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null; // This component only handles DOM manipulation
}
